import { useState, useRef } from 'react';
import { Upload, FileJson, AlertCircle, Check, Building2, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PricingRule, AvailabilityRule, Property } from '@/types/revenue-manager';

interface ImportRulesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  ruleType: 'pricing' | 'availability' | 'events';
  properties: Property[];
  currentPropertyId: string;
  onImport: (rules: any[], targetPropertyIds: string[]) => Promise<void>;
}

export function ImportRulesDialog({
  open,
  onOpenChange,
  ruleType,
  properties,
  currentPropertyId,
  onImport,
}: ImportRulesDialogProps) {
  const [jsonInput, setJsonInput] = useState('');
  const [parsedData, setParsedData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedProperties, setSelectedProperties] = useState<string[]>([currentPropertyId]);
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetState = () => {
    setJsonInput('');
    setParsedData(null);
    setError(null);
    setSelectedProperties([currentPropertyId]);
    setIsImporting(false);
    setImportError(null);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      setJsonInput(content);
      parseJson(content);
    };
    reader.readAsText(file);
  };

  const parseJson = (json: string) => {
    try {
      const data = JSON.parse(json);
      
      if (!data.rules || !Array.isArray(data.rules)) {
        throw new Error('Invalid format: missing rules array');
      }
      
      if (data.ruleType !== ruleType) {
        throw new Error(`Rule type mismatch: expected ${ruleType}, got ${data.ruleType}`);
      }

      setParsedData(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid JSON format');
      setParsedData(null);
    }
  };

  const toggleProperty = (propertyId: string) => {
    setSelectedProperties((prev) =>
      prev.includes(propertyId)
        ? prev.filter((id) => id !== propertyId)
        : [...prev, propertyId]
    );
  };

  const handleImport = async () => {
    if (parsedData?.rules && selectedProperties.length > 0) {
      setIsImporting(true);
      setImportError(null);
      
      try {
        await onImport(parsedData.rules, selectedProperties);
        // Only close and reset on success
        onOpenChange(false);
        resetState();
      } catch (err) {
        // Keep modal open and show error
        setImportError(err instanceof Error ? err.message : 'Failed to import. Please try again.');
        setIsImporting(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => { onOpenChange(open); if (!open) resetState(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Import {ruleType === 'pricing' ? 'Pricing Rules' : ruleType === 'availability' ? 'Availability Rules' : 'Events'}
          </DialogTitle>
          <DialogDescription>
            Import {ruleType === 'events' ? 'events' : 'rules'} and apply them to one or multiple properties
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {/* File Upload */}
          <div className="space-y-2">
            <Label>Upload JSON File</Label>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".json"
              className="hidden"
            />
            <Button
              variant="outline"
              className="w-full h-20 border-dashed"
              onClick={() => fileInputRef.current?.click()}
            >
              <div className="flex flex-col items-center gap-1">
                <FileJson className="h-6 w-6 text-muted-foreground" />
                <span className="text-sm">Click to upload or drag & drop</span>
              </div>
            </Button>
          </div>

          {/* Or paste JSON */}
          <div className="space-y-2">
            <Label>Or Paste JSON</Label>
            <Textarea
              placeholder='{"ruleType": "pricing", "rules": [...]}'
              value={jsonInput}
              onChange={(e) => {
                setJsonInput(e.target.value);
                if (e.target.value.trim()) {
                  parseJson(e.target.value);
                } else {
                  setParsedData(null);
                  setError(null);
                }
              }}
              rows={4}
              className="font-mono text-sm"
            />
          </div>

          {/* Parsing Error Display */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Import Error Display */}
          {importError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{importError}</AlertDescription>
            </Alert>
          )}

          {/* Success / Parsed Data */}
          {parsedData && (
            <Alert className="bg-status-active-bg border-status-active">
              <Check className="h-4 w-4 text-status-active" />
              <AlertDescription className="text-status-active">
                Found {parsedData.rules.length} rules from {parsedData.sourceProperty || 'unknown source'}
              </AlertDescription>
            </Alert>
          )}

          {/* Target Properties Selection */}
          {parsedData && (
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Apply to Properties
              </Label>
              <div className="rounded-lg border border-border max-h-48 overflow-y-auto">
                {properties.map((property) => (
                  <div
                    key={property.id}
                    className={`flex items-center gap-3 p-3 border-b border-border last:border-0 cursor-pointer transition-colors ${
                      selectedProperties.includes(property.id) ? 'bg-accent/10' : 'hover:bg-muted'
                    }`}
                    onClick={() => toggleProperty(property.id)}
                  >
                    <Checkbox
                      checked={selectedProperties.includes(property.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedProperties(prev => [...prev, property.id]);
                        } else {
                          setSelectedProperties(prev => prev.filter(id => id !== property.id));
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex-1">
                      <p className="font-medium">{property.name}</p>
                      <p className="text-sm text-muted-foreground">{property.location}</p>
                    </div>
                    {property.id === currentPropertyId && (
                      <span className="text-xs bg-muted px-2 py-0.5 rounded">Current</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-sm text-muted-foreground">
                {selectedProperties.length} {selectedProperties.length === 1 ? 'property' : 'properties'} selected
              </p>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); resetState(); }}>
            Cancel
          </Button>
          <Button 
            onClick={handleImport} 
            disabled={!parsedData || selectedProperties.length === 0 || isImporting}
          >
            {isImporting ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Upload className="mr-2 h-4 w-4" />
            )}
            {isImporting ? 'Importing...' : `Import ${parsedData?.rules?.length || 0} ${ruleType === 'events' ? 'Events' : 'Rules'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
